import type { BrandSpecials } from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { CompareProduct } from '@iheartjane/menu';
import { hideStrikethroughPrice, productPhotoMetadata } from '@iheartjane/menu';
import type { Nullable } from '@iheartjane/types';

import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';
import { getGoldTagLabel } from '@jane/bloom/utils/badges/getGoldLabel';
import { getDefaultWeightPricing } from '@jane/bloom/viewModels/buildProductCardProps';
import { buildCompareProductPayload } from '@jane/bloom/viewModels/compareProductPayload';
import { hasSpecialApplied } from '@jane/bloom/viewModels/getPrice';
import type { ProductViewModel } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import { buildSingleProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearchResult';
import type { AlgoliaProduct } from '@jane/search/types';

type BuildCompareProductArgs = {
  brandSpecials?: BrandSpecials;
  product: ProductSearchResultViewModel;
  smartProductInstance?: SmartSortProduct<AlgoliaProduct>;
  /** Store state (e.g. "CA") — used to honor per-store strikethrough suppression */
  storeState?: string | null;
};

export const buildCompareProduct = ({
  brandSpecials = [],
  product,
  smartProductInstance,
  storeState,
}: BuildCompareProductArgs): CompareProduct => {
  const { url: imageUrl } = productPhotoMetadata(product);

  const { priceIsDiscounted } = getDefaultWeightPricing(product);
  const isDiscounted = Boolean(hasSpecialApplied(product)) && priceIsDiscounted;
  const showStrikethrough =
    isDiscounted && !hideStrikethroughPrice(storeState ?? '');

  return {
    activities: product.activities ?? [],
    aggregateRating: product.aggregate_rating ?? null,
    availableWeights: product.available_weights ?? [],
    brand: product.brand ?? null,
    category: product.category ?? null,
    dosage: product.dosageAmountPackInfo || null,
    feelings: product.feelings ?? [],
    flavors:
      product.flavors?.flatMap((flavor) =>
        flavor.title ? [flavor.title] : []
      ) ?? [],
    goldTagLabel: getGoldTagLabel(product, brandSpecials),
    id: product.product_id,
    imageUrl,
    kind: product.kind,
    name: product.name,
    originalPrice: showStrikethrough ? product.priceLabel || null : null,
    payload: buildCompareProductPayload({ product, smartProductInstance }),
    percentCbd:
      product.percent_cbd != null ? Number(product.percent_cbd) : null,
    percentThc:
      product.percent_thc != null ? Number(product.percent_thc) : null,
    price: product.discountedPriceLabel || null,
    reviewCount:
      product.review_count != null ? Number(product.review_count) : null,
    specialLabel: product.specialLabel || null,
    url: `/products/${product.product_id}/${product.unique_slug ?? product.id}`,
  };
};

type BuildCompareProductFromDetailArgs = {
  brandSpecials?: BrandSpecials;
  bundleTag?: boolean;
  cart?: Nullable<Cart>;
  customLanguage: CustomLanguage;
  pinnedStore?: Nullable<BloomMenuStore>;
  product: ProductViewModel;
  shouldShowGold: boolean;
  storeSpecials: Nullable<StoreSpecials>;
};

/**
 * PDP variant of {@link buildCompareProduct}.
 * The PDP loader has raw product data, so we need to build the view model first.
 */
export const buildCompareProductFromDetail = ({
  brandSpecials = [],
  bundleTag,
  cart,
  customLanguage,
  pinnedStore,
  product,
  shouldShowGold,
  storeSpecials,
}: BuildCompareProductFromDetailArgs): CompareProduct => {
  const productViewModel = buildSingleProductSearchResultViewModel({
    bundleTag,
    cart,
    customLanguage,
    pinnedStore,
    precalculatedShouldShowGold: shouldShowGold,
    product: { ...product, bucket_price: Number(product.bucket_price) },
    specials: storeSpecials,
  });

  return buildCompareProduct({
    brandSpecials,
    product: productViewModel,
    storeState: pinnedStore?.state,
  });
};
